<template>
  <v-dialog :value="value" max-width="900" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="text-h6">Pawer Pricing</v-card-title>
      <v-tabs v-model="tab" class="px-4">
        <v-tab v-for="country in countries" :key="country.value">{{ country.text }}</v-tab>
      </v-tabs>
      <v-divider />
      <v-card-text class="pt-4">
        <v-progress-linear v-if="loading" indeterminate class="mb-3" />
        <v-alert v-if="error" type="error" text dense>{{ error }}</v-alert>
        <template v-if="grid">
          <p class="text-body-2 mb-4">
            Valores mensuales netos en {{ grid.currency }}, canal directo, sin descuento. Cada plan cobra al menos
            {{ minimumUsd }} al mes.
          </p>

          <h4 class="text-subtitle-1 font-weight-bold mb-2">Employee Benefit</h4>
          <v-row dense class="mb-4">
            <v-col v-for="table in ebTables" :key="table.key" cols="12" md="6">
              <div class="text-caption mb-1">{{ table.title }}</div>
              <v-simple-table dense class="pawer-pricing__table">
                <thead>
                  <tr>
                    <th>Colaboradores</th>
                    <th v-for="plan in table.plans" :key="plan" class="text-right">{{ planLabel(plan) }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in table.rows" :key="row.from">
                    <td>{{ rangeText(row) }}</td>
                    <td v-for="plan in table.plans" :key="plan" class="text-right">{{ money(row.prices[plan]) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>

          <h4 class="text-subtitle-1 font-weight-bold mb-2">Asistencias (capitado, por asegurado)</h4>
          <v-row dense>
            <v-col v-for="table in grid.assistance" :key="table.key" cols="12" md="6">
              <div class="text-caption mb-1">{{ assistanceTitle(table) }}</div>
              <v-simple-table dense class="pawer-pricing__table">
                <thead>
                  <tr>
                    <th>Asegurados</th>
                    <th v-for="plan in table.plans" :key="plan" class="text-right">{{ planLabel(plan) }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in table.rows" :key="row.from">
                    <td>{{ rangeText(row) }}</td>
                    <td v-for="plan in table.plans" :key="plan" class="text-right">{{ money(row.prices[plan]) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import B2bQuotesService from '../services/b2b-quotes.service';
import { COUNTRY_OPTIONS, PLAN_OPTIONS, errorMessage, formatMoney } from '../utils/b2b-quotes.utils';

/**
 * Pawer's standard prices by country, tier by tier: Employee Benefit
 * (collective and Flexible pool) and Asistencias per insured. The backend
 * prices them with the quote engine, so they match what the cotizador quotes.
 */
export default {
  name: 'PawerPricingModal',
  props: {
    value: { type: Boolean, default: false },
    /** Monthly minimum of a plan, worded in USD ("USD 99"). */
    minimumUsd: { type: String, default: 'USD 99' }
  },
  data() {
    return { tab: 0, countries: COUNTRY_OPTIONS, grids: {}, loading: false, error: '' };
  },
  computed: {
    countryIso() {
      return this.countries[this.tab]?.value ?? 'CL';
    },
    grid() {
      return this.grids[this.countryIso] ?? null;
    },
    ebTables() {
      if (!this.grid) return [];
      const { collective, pool } = this.grid.employeeBenefit;
      return [
        { ...collective, title: 'Colectivo (todos los colaboradores) · por membresía' },
        { ...pool, title: 'Flexible (bolsa) · por membresía de la bolsa' }
      ];
    }
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
    tab() {
      this.load();
    }
  },
  methods: {
    async load() {
      const countryIso = this.countryIso;
      if (this.grids[countryIso]) return;
      this.loading = true;
      this.error = '';
      const res = await B2bQuotesService.getPriceGrid(countryIso);
      this.loading = false;
      if (res.status === 'ok') this.$set(this.grids, countryIso, res.data);
      else this.error = errorMessage(res.code);
    },
    planLabel(code) {
      return PLAN_OPTIONS.find((p) => p.value === code)?.text ?? code;
    },
    money(amount) {
      return formatMoney(amount, this.grid.currency);
    },
    rangeText(row) {
      const from = row.from.toLocaleString('es-CL');
      return row.to === null ? `${from}+` : `${from} – ${row.to.toLocaleString('es-CL')}`;
    },
    assistanceTitle(table) {
      const product = table.product === 'pet_insurance' ? 'Seguro de mascotas' : 'Seguro de otra cosa';
      const activation = Math.round(table.activation * 100);
      return `${product} · ${activation}% de los asegurados activa una mascota`;
    }
  }
};
</script>

<style scoped>
.pawer-pricing__table {
  border: 1px solid var(--pw-border);
  border-radius: 8px;
}
</style>
